import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Lock, Loader2, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
        setTokenValid(true);
        setMaskedEmail(response.data.email);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters / 密碼至少需要6個字符');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match / 密碼不一致');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
        confirm_password: confirmPassword
      });
      setSuccess(true);
      toast.success('Password reset successful! / 密碼重設成功！');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password / 重設密碼失敗');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-fac-dark flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-fac-green animate-spin mx-auto mb-4" />
          <p className="text-white font-chinese">Verifying reset link... / 驗證重設連結中...</p>
        </div>
      </div>
    );
  }

  // No token or invalid token
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-fac-dark flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="bg-fac-card border-border">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-fac-red/10 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-fac-red" />
              </div>
              <CardTitle className="text-2xl font-heading text-white uppercase">
                Invalid Link
              </CardTitle>
              <CardDescription className="text-muted-foreground font-chinese">
                無效連結 / The reset link is invalid or has expired
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-center text-muted-foreground font-chinese">
                This password reset link is either invalid or has expired. Please request a new one.
                <br />
                此密碼重設連結無效或已過期，請重新請求。
              </p>
              
              <div className="flex flex-col gap-3">
                <Link to="/forgot-password">
                  <Button className="w-full bg-fac-green text-black font-bold">
                    Request New Link / 請求新連結
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="w-full border-fac-green text-fac-green hover:bg-fac-green/10">
                    Back to Login / 返回登入
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-fac-dark flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="bg-fac-card border-border">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-fac-green/10 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-fac-green" />
              </div>
              <CardTitle className="text-2xl font-heading text-white uppercase">
                Password Reset!
              </CardTitle>
              <CardDescription className="text-muted-foreground font-chinese">
                密碼已重設 / Your password has been updated
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-center text-white font-chinese">
                Your password has been successfully reset. You can now login with your new password.
                <br />
                您的密碼已成功重設，您現在可以使用新密碼登入。
              </p>
              
              <Button 
                onClick={() => navigate('/login')}
                className="w-full bg-fac-green text-black font-bold uppercase tracking-wider"
              >
                Go to Login / 前往登入
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen bg-fac-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="flex items-center gap-2 text-fac-green hover:text-fac-green-dim mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="font-chinese">Back to Login / 返回登入</span>
        </Link>

        <Card className="bg-fac-card border-border">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-fac-green/10 flex items-center justify-center">
              <Lock className="w-8 h-8 text-fac-green" />
            </div>
            <CardTitle className="text-2xl font-heading text-white uppercase">
              Reset Password
            </CardTitle>
            <CardDescription className="text-muted-foreground font-chinese">
              重設密碼 / Enter your new password
            </CardDescription>
            {maskedEmail && (
              <p className="text-sm text-fac-green mt-2">
                Account: {maskedEmail}
              </p>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white font-chinese">
                  New Password / 新密碼
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-fac-dark border-border text-white pr-10"
                    data-testid="reset-password-input"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground font-chinese">
                  Minimum 6 characters / 至少6個字符
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-white font-chinese">
                  Confirm Password / 確認密碼
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-fac-dark border-border text-white pr-10"
                    data-testid="reset-confirm-password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {password && confirmPassword && password !== confirmPassword && (
                <p className="text-fac-red text-sm font-chinese">
                  Passwords do not match / 密碼不一致
                </p>
              )}

              <Button
                type="submit"
                disabled={loading || password !== confirmPassword}
                className="w-full bg-fac-green text-black font-bold uppercase tracking-wider hover:bg-fac-green-dim"
                data-testid="reset-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting... / 重設中...
                  </>
                ) : (
                  'Reset Password / 重設密碼'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
